import currency from 'currency.js';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import moment from 'moment-timezone';

export type Formatter<T> = (v: T) => string;
export type NumberFormatter = Formatter<number>;
export type StringFormatter = Formatter<string>;
export type DayjsFormatter = Formatter<dayjs.Dayjs>;
export type MomentFormatter = Formatter<moment.Moment>;
export type DecimalFormatter = Formatter<Decimal>;
export type PhoneFormatter = (v: string, c?: string) => string;
export type TextInputFormatter<T> = {
  serialize: Formatter<T>;
  deserialize: (s: string) => T;
};

interface AddressType {
  streetAddress: string;
  unit?: string;
  locality: string;
  subdivision: string;
  zip: string;
}
export type AddressFormatter = Formatter<AddressType>;
interface NameType {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}
export type NameFormatter = Formatter<NameType>;
export type BankAccountFormatter = Formatter<{ institutionName: string; accountNumber: string | null }>;
export type CardholderFormatter = Formatter<{ embossedName: string | null }>;

export const MICRO: number = 1_000_000;

const MoneyCentGranularityNumberFormat = (currencyCode: string) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const USDMoneyCentGranularityNumberFormat = MoneyCentGranularityNumberFormat('USD');

export const GenericMoneyCentGranularityMicro = (v: number, currencyCode: string): string => {
  return MoneyCentGranularityNumberFormat(currencyCode).format(v / MICRO);
};

export const MoneyCentGranularity: NumberFormatter = (v) => USDMoneyCentGranularityNumberFormat.format(v);
export const MoneyCentGranularityMicro: NumberFormatter = (v) => USDMoneyCentGranularityNumberFormat.format(v / MICRO);

const CentsNumberFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const CentsMicro: NumberFormatter = (v) => `${CentsNumberFormat.format((v * 100) / MICRO)}¢`;

const MoneySignedCentGranularityNumberFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  signDisplay: 'exceptZero',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const MoneySignedCentGranularityMicro: NumberFormatter = (v) =>
  MoneySignedCentGranularityNumberFormat.format(v / MICRO);

export const moneyToMicro = (s: string): number => {
  const cleaned = s.replace(/(\$|\,|\.)/g, '');
  const parsed = parseInt(cleaned);
  if (isNaN(parsed)) {
    return 0;
  }
  // Cents
  if (parsed.toString().length < 2) {
    return parseInt(cleaned) * 10000;
  }
  // Dollars
  const l = cleaned.length;
  let _n: string[] = [];
  const centStart = l - 2;
  for (let i = l - 1; i > -1; i--) {
    const d = cleaned[i];
    _n.push(d);
    if (i === centStart) {
      _n.push('.');
    }
  }
  const dollarStr = _n.reverse().join('');
  return currency(dollarStr).multiply(MICRO).value;
};

export const MoneyTextInputFormatter: TextInputFormatter<number> = {
  serialize: MoneyCentGranularityMicro,
  deserialize: moneyToMicro,
};

const MoneyDollarGranularityNumberFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const MoneyDollarGranularity: NumberFormatter = (v) => MoneyDollarGranularityNumberFormat.format(v);
export const MoneyDollarGranularityMicro: NumberFormatter = (v) => MoneyDollarGranularityNumberFormat.format(v / MICRO);
export const MoneyRoundedDollarGranularityMicro: NumberFormatter = (v) =>
  MoneyDollarGranularityNumberFormat.format(Math.round(v / MICRO));
export const MoneyThousandDollarGranularityMicro: NumberFormatter = (v) =>
  `${MoneyDollarGranularityNumberFormat.format(v / MICRO / 1000)}K`;

// If amount is a whole thousand dollars use thousand dollar granularity, otherwise use dollar granularity
export const MoneyMaybeThousandDollarGranularityMicro: NumberFormatter = (v) => {
  if (v % (1000 * MICRO) === 0) {
    return MoneyThousandDollarGranularityMicro(v);
  }
  return MoneyDollarGranularityMicro(v);
};

/**
 * Formats a number as a dollar amount string, abbreviated for large numbers.
 * Examples:
 *   MoneyMagnitudeGranularity(532)        // "$532"
 *   MoneyMagnitudeGranularity(45200)      // "$45.2K"
 *   MoneyMagnitudeGranularity(-14420000)   // "-$14.2M"
 *   MoneyMagnitudeGranularity(9210000000) // "$9.21B"
 */
export const MoneyMagnitudeGranularity: NumberFormatter = (amt: number): string => {
  if (amt < 1e3) return MoneyCentGranularity(amt);
  if (amt < 1e6) return MoneyCentGranularity(amt / 1e3) + 'K';
  if (amt < 1e9) return MoneyCentGranularity(amt / 1e6) + 'M';
  else return MoneyCentGranularity(amt / 1e9) + 'B';
};

/**
 * Similar to MoneyMagnitudeGranularity, but uses micros instead of dollars.
 * The returned value will not include cent granularity, the input is expected to be whole.
 * Examples:
 *   MoneyMagnitudeGranularityMicro(1,000,000,000)        // "$1K"
 *   MoneyMagnitudeGranularityMicro(1,000,000,000,000)      // "$1M"
 */
export const MoneyDollarMagnitudeGranularityMicro: NumberFormatter = (v: number): string => {
  const amt = v / MICRO;
  if (amt < 1e3) return MoneyDollarGranularity(amt);
  if (amt < 1e6) return MoneyDollarGranularity(amt / 1e3) + 'K';
  if (amt < 1e9) return MoneyDollarGranularity(amt / 1e6) + 'M';
  else return MoneyDollarGranularity(amt / 1e9) + 'B';
};

// If amount is a whole dollar use dollar granularily, otherwise use cent granularity
export const MoneyRoundedGranularityMicro: NumberFormatter = (v) => {
  if (v % MICRO != 0) {
    return MoneyCentGranularityMicro(v);
  }
  return MoneyDollarGranularityMicro(v);
};

export const DollarCentsAmountToMicro = (v: number): number => {
  return currency(v).multiply(MICRO).value;
};

export const moneyDollarToMicro = (s: string) => {
  const cleaned = s.replace(/\D/g, '');
  const parsed = parseInt(cleaned);

  return isNaN(parsed) ? 0 : DollarCentsAmountToMicro(parsed);
};

export const MoneyDollarTextInputFormatter: TextInputFormatter<number> = {
  serialize: MoneyDollarGranularityMicro,
  deserialize: moneyDollarToMicro,
};

const IntWithCommasNumberFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const IntWithCommas: NumberFormatter = (v) => IntWithCommasNumberFormat.format(v);
export const IntWithCommasMicro: NumberFormatter = (v) => IntWithCommasNumberFormat.format(v / MICRO);

// Typescript version of https://stackoverflow.com/a/58240882
const NumberWords: { [key: number | string]: string } = {
  1: 'One',
  2: 'Two',
  3: 'Three',
  4: 'Four',
  5: 'Five',
  6: 'Six',
  7: 'Seven',
  8: 'Eight',
  9: 'Nine',
  10: 'Ten',
  11: 'Eleven',
  12: 'Twelve',
  t3: 'Thir',
  t5: 'Fif',
  t8: 'Eigh',
  20: 'Twenty',
};
export const IntToWords: NumberFormatter = (v) => {
  const word: string[] = [];
  const hundreds = 0 | ((v % 1000) / 100);
  const tens = 0 | ((v % 100) / 10);
  const ones = v % 10;
  let part = '';

  if (v === 0) return 'Zero';
  if (hundreds) word.push(NumberWords[hundreds] + ' Hundred');

  if (tens === 0) {
    word.push(NumberWords[ones]);
  } else if (tens === 1) {
    word.push(NumberWords['1' + ones] || (NumberWords['t' + ones] || NumberWords[ones]) + 'teen');
  } else {
    part = NumberWords[tens + '0'] || (NumberWords['t' + tens] || NumberWords[tens]) + 'ty';
    word.push(NumberWords[ones] ? part + '-' + NumberWords[ones] : part);
  }
  return word.join(' ');
};

const MaxTwoSigFigsIntegerFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const TwoDecimalsFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const MaxTwoSigFigs: NumberFormatter = (v) => {
  if (v >= 1) {
    return MaxTwoSigFigsIntegerFormat.format(v);
  } else {
    return v.toPrecision(2);
  }
};

export const TwoDecimals: NumberFormatter = (v) => {
  if (v >= 1) {
    return TwoDecimalsFormat.format(v);
  } else {
    return v.toPrecision(2);
  }
};

// Shows up to 3 decimal places if non-zero values will be displayed within those 3 places
// Otherwise, displays up to 8 decimal places
export const SharesFormatter: NumberFormatter = (shares) => {
  return Math.trunc(shares * 100) % 100 === 0
    ? VariableSigFigsFormatter(8).format(shares)
    : VariableSigFigsFormatter(3).format(shares);
};

export const VariableSigFigsFormatter = (precision: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: precision,
  });

export const Points: NumberFormatter = (v) => `${IntWithCommas(v)} Points`;
export const PointMultiplier: NumberFormatter = (v) => `${v}X`;
export const RhPointMultiplier: NumberFormatter = (v) => `${v}x`;

const PercentNumberFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const PercentSignedNumberFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'exceptZero',
});

const PercentNumberShortFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const PercentNumberWholeFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const ApyFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

export const Percent: NumberFormatter = (v) => PercentNumberFormat.format(v);
export const PercentMicro: NumberFormatter = (v) => PercentNumberFormat.format(v / MICRO);
export const PercentShort: NumberFormatter = (v) => PercentNumberShortFormat.format(v);
export const PercentShortMicro: NumberFormatter = (v) => PercentNumberShortFormat.format(v / MICRO);
export const PercentWhole: NumberFormatter = (v) => PercentNumberWholeFormat.format(v);
export const PercentWholeMicro: NumberFormatter = (v) => PercentNumberWholeFormat.format(v / MICRO);
export const ApyFormatMicro = (v: number) => ApyFormat.format(v / MICRO);
export const PercentSigned: NumberFormatter = (v) => PercentSignedNumberFormat.format(v);
export const Percentage: NumberFormatter = (v: number) => {
  return (v * 100).toFixed(2) + '%';
};
export const RawPercentage: NumberFormatter = (v: number) => {
  return v.toFixed(2) + '%';
};
export const PercentageMicro: NumberFormatter = (percentMicro: number) => {
  const negation = percentMicro < 0 ? '-' : '';
  const shifted = Math.abs(percentMicro) / MICRO;
  return `${negation}${Percentage(shifted)}`;
};

export const Hours: NumberFormatter = (v) => {
  const hours = Math.floor(v / 1000 / 60 / 60);
  const suffix = hours < 2 ? 'Hour' : 'Hours';
  return `${hours} ${suffix}`;
};

export const HoursAbbreviated: NumberFormatter = (v) => {
  const hours = Math.floor(v / 1000 / 60 / 60);
  return `${hours}h`;
};

export const TransactionAt: MomentFormatter = (v) => v.format('dddd MMM D, h:mm A');
export const TransactionAtYear: MomentFormatter = (v) => v.format('dddd MMM D YYYY, h:mm A');
export const CancellationDate: MomentFormatter = (v) => v.format('MMM D h:mm A z');
export const DateMonthYear: MomentFormatter = (v) => v.format('MMM DD, YYYY');
export const DateMonthYearShort: MomentFormatter = (v) => v.format('MMM D YYYY');
export const DateMonthYearShortest: MomentFormatter = (v) => v.format('MMM D YY');
export const DateMonth: MomentFormatter = (v) => v.format('MMMM Do');
export const DateMonthYearFromNow: MomentFormatter = (v) =>
  v.calendar(null, {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'MMM DD, YYYY',
    lastDay: '[Yesterday]',
    lastWeek: 'MMM DD, YYYY',
    sameElse: 'MMM DD, YYYY',
  });
export const DateShort: MomentFormatter = (v) =>
  v.calendar(null, {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'MMM D',
    lastDay: '[Yesterday]',
    lastWeek: 'MMM D',
    sameElse: 'MMM D',
  });
export const DateShortInline: MomentFormatter = (v) =>
  v.calendar(null, {
    sameDay: '[today]',
    nextDay: '[tomorrow]',
    nextWeek: 'MMM D',
    lastDay: '[yesterday]',
    lastWeek: 'MMM D',
    sameElse: 'MMM D',
  });
export const DayMonthAndYear: MomentFormatter = (v) => v.format('D MMM YYYY');
export const DayMonthAndYearDOB: MomentFormatter = (v) => v.format('MM / DD / YYYY');
export const DayMonthAndYearShort: MomentFormatter = (v) => v.format('MM/DD/YY');
export const ShortMonthAndYear: MomentFormatter = (v) => v.format('MM/YY');
export const ShortMonthDay: MomentFormatter = (v) => v.format('MM/DD');
export const ShortMonth: MomentFormatter = (v) => v.format('MM');
export const ShortYear: MomentFormatter = (v) => v.format('YY');
export const MonthYear: MomentFormatter = (v) => v.format('MMMM YYYY');
export const MonthDashYear: MomentFormatter = (v) => v.format('MMMM-YYYY');
export const MonthDay: MomentFormatter = (v) => v.format('MMM D');
export const MonthDayYear: MomentFormatter = (v) => v.format('MM/DD/YYYY');
export const MonthDayTime: MomentFormatter = (v) => v.format('MMM D h:mm A');
export const Day: MomentFormatter = (v) => v.format('Do');
export const Weekday: MomentFormatter = (v) => v.format('dddd');
export const WeekdayMonthDate: MomentFormatter = (v) =>
  v.calendar(null, {
    sameDay: '[today]',
    nextDay: '[tomorrow]',
    nextWeek: 'dddd MMM D',
    lastDay: '[yesterday]',
    lastWeek: 'dddd MMM D',
    sameElse: 'dddd MMM D',
  });
export const WeekdayMonthDateTitle: MomentFormatter = (v) =>
  v.calendar(null, {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'dddd MMM D',
    lastDay: '[Yesterday]',
    lastWeek: 'dddd MMM D',
    sameElse: 'dddd MMM D',
  });
export const TruncatedWeekdayMonthDateTitle: MomentFormatter = (v) =>
  v.calendar(null, {
    sameDay: 'ddd, MMM D',
    nextDay: 'ddd, MMM D',
    nextWeek: 'ddd, MMM D',
    lastDay: 'ddd, MMM D',
    lastWeek: 'ddd, MMM D',
    sameElse: 'ddd, MMM D',
  });
export const MonthLong: MomentFormatter = (v) => v.format('MMMM');
export const MonthFullDate: MomentFormatter = (v) => v.format('MMMM D');
export const MonthDuration: MomentFormatter = (v) =>
  moment(v).subtract(1, 'month').add(1, 'day').format('MMM D') + ' - ' + v.format('MMM D');
export const MonthDate: MomentFormatter = (v) => v.format('MMM D');
export const MonthDateYear: MomentFormatter = (v) => v.format('MMM DD YYYY');
export const MonthDateYearWithComma: MomentFormatter = (v) => v.format('MMM DD, YYYY');
export const MonthDateYearFull: MomentFormatter = (v) => v.format('MMMM DD, YYYY');
export const MonthDateFromNow: MomentFormatter = (v) =>
  v.calendar(null, {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'MMM D',
    lastDay: '[Yesterday]',
    lastWeek: 'MMM D',
    sameElse: 'MMM D',
  });
export const YearMonthDate: MomentFormatter = (v) => v.format('YYYY-MM-DD');
export const DayjsYearMonthDate: DayjsFormatter = (v) => v.format('YYYY-MM-DD');
export const YearMonthDateFromNow: MomentFormatter = (v) =>
  v.calendar(null, {
    sameDay: '[Today]',
    nextDay: '[Tomorrow]',
    nextWeek: 'YYYY-MM-DD',
    lastDay: '[Yesterday]',
    lastWeek: 'YYYY-MM-DD',
    sameElse: 'YYYY-MM-DD',
  });
export const YearMonthDateTime: MomentFormatter = (v) => v.format('YYYY-MM-DD HH:mm:ss');
export const Time: MomentFormatter = (v) => v.format('h:mm A');
export const TimeString: StringFormatter = (v) => moment(v, 'HH:mm').format('h:mm A');
export const TimeDate: MomentFormatter = (v) => `${Time(v)} ${DateMonthYearFromNow(v)}`;
export const DateAtUTC: (date: Date | moment.Moment) => moment.Moment = (v) => moment(v).utc(true);

/**
 * Formats a time interval between two timestamps with smart AM/PM display.
 * Only shows AM/PM once at the end if both times are in the same period.
 * @param start - Start time in milliseconds
 * @param end - End time in milliseconds
 * @param consolidateAMPM - Whether to consolidate AM/PM if both times are in the same period
 * @returns Formatted time range (e.g., "2:30 – 3:45 PM" or "10:30 AM – 2:15 PM")
 */
export const FormattedTimeInterval = (start: number, end: number, consolidateAMPM: boolean = false): string => {
  const startMoment = moment(start).local();
  const endMoment = moment(end).local();

  // Check if both times are in the same AM/PM period
  const startPeriod = startMoment.format('A');
  const endPeriod = endMoment.format('A');

  if (startPeriod === endPeriod && consolidateAMPM) {
    // Same AM/PM period: show AM/PM only once at the end
    const startTime = startMoment.format('h:mm');
    const endTime = endMoment.format('h:mm A');
    return `${startTime} – ${endTime}`;
  } else {
    // Different AM/PM periods: show both
    const startTime = startMoment.format('h:mm A');
    const endTime = endMoment.format('h:mm A');
    return `${startTime} – ${endTime}`;
  }
};

/* Converts milliseconds to largest time unit */
export const MillisToTimeInterval: NumberFormatter = (v) => {
  let s = v / 1000;
  let interval = s / 31536000;
  if (interval >= 1) {
    return Math.floor(interval) + 'y';
  }
  interval = s / 2592000;
  if (interval >= 1) {
    return Math.floor(interval) + 'mo';
  }
  interval = s / 86400;
  if (interval >= 1) {
    return Math.floor(interval) + 'd';
  }
  interval = s / 3600;
  if (interval >= 1) {
    return Math.floor(interval) + 'h';
  }
  interval = s / 60;
  if (interval >= 1) {
    return Math.floor(interval) + 'm';
  }
  return Math.floor(s) + 's';
};

// TODO (X1CARD-290): Ensure that time-dependent values are live
export const TimeSince: MomentFormatter = (v) => {
  const millis = Math.floor(Date.now() - v.valueOf());
  return MillisToTimeInterval(millis);
};

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;
export const DurationToDays: NumberFormatter = (v) => {
  return (v / MILLISECONDS_IN_DAY).toFixed(0);
};

export const E164ToNational: PhoneFormatter = (v, c = '+1') => {
  const digits = v.replace(c, '').replace(/\D+/g, '');
  if (digits.length === 0) {
    return '';
  } else if (digits.length < 4) {
    return `(${digits.slice(0, 3)}`;
  } else if (digits.length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}`;
  } else {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};
export const NationalToE164: PhoneFormatter = (v, c = '+1') => `${c}${v.replace(/\D+/g, '')}`;

export const Before: MomentFormatter = (v) => `Before ${v.format('MMM D')}`;
export const Completed: MomentFormatter = (v) => `Completed ${v.fromNow()}`;
export const XMoreDays: MomentFormatter = (v) => `${v.diff(moment(), 'days')}`;

// Excepted behavior defined here:
// https://www.figma.com/file/tbb9ArYANgn6hBfTZ2gSar/%F0%9F%92%B3%F0%9F%93%B1-Credit-Mobile-App?type=design&node-id=9429-115684&mode=design&t=bQr7OeMAaGHa9kjU-0
export const TimeAgo: MomentFormatter = (v) => {
  const duration = moment.duration(moment().diff(moment(v)));
  if (duration.asMinutes() < 1 || moment().isBefore(moment(v))) {
    return '1m ago';
  }
  if (duration.asHours() < 1) {
    return `${duration.minutes()}m ago`;
  }
  if (duration.asDays() < 1) {
    return `${duration.hours()}h ago`;
  }
  if (duration.asWeeks() < 1) {
    return `${duration.days()}d ago`;
  }
  if (duration.asWeeks() < 2) {
    return '1w ago';
  }
  if (duration.asYears() < 1) {
    return moment(v).format('MMM D');
  }
  return moment(v).format('MMM D, YYYY');
};

const MAX_INSTITUTION_NAME_LENGTH = 20;
export const TruncatedInstitutionName: StringFormatter = (v) => {
  return v.length > MAX_INSTITUTION_NAME_LENGTH ? `${v.substr(0, 15).trim()}\u2026` : v;
};

export const LastFourDigits: StringFormatter = (v) => v.substring(v.length - 4);
export const MaskedInstitutionAccountNumber: StringFormatter = (v) => `••••${LastFourDigits(v)}`;
// Use this for card, account, and routing numbers
export const SensitiveNumber: StringFormatter = (_) => _.replace(/(\d{4})/g, '$1 ').trim();
export const MaskedSensitiveNumber: StringFormatter = (v) => `••••${LastFourDigits(v)}`;
export const MaskedSensitiveNumberWithSpaces: StringFormatter = (v) => `•••• ${LastFourDigits(v)}`;
export const Address: AddressFormatter = (v) => {
  const unitPart = v.unit != null && v.unit !== '' ? `, ${v.unit.trim()}` : '';
  return `${v.streetAddress.trim()}${unitPart},  ${v.locality.trim()} ${v.subdivision.trim()} ${v.zip.trim()}`;
};

export const AddressParts = (v: AddressType): { addressLine1: string; addressLine2: string } => {
  const unitPart = !!v.unit?.trim() ? `, ${v.unit.trim()}` : '';
  const cityStr = Boolean(v.locality.trim()) ? `${v.locality.trim()},` : '';
  return {
    addressLine1: `${v.streetAddress.trim()}${unitPart}`,
    addressLine2: [cityStr, v.subdivision.trim(), v.zip.trim()].filter(Boolean).join(' '),
  };
};

export const Initials: NameFormatter = (v) => `${v.firstName?.charAt(0)}${v.lastName?.charAt(0)}`.toUpperCase();

export const FullName: NameFormatter = (n) => {
  return [n.firstName, n.middleName, n.lastName]
    .map((_) => _?.trim())
    .filter(Boolean)
    .join(' ');
};

export const BankNameAccountNumber: BankAccountFormatter = ({ institutionName, accountNumber }) => {
  const cleanedName = institutionName.trim();
  const hasAccountNumber = accountNumber != null && accountNumber.trim() != '';
  const name = hasAccountNumber ? TruncatedInstitutionName(cleanedName) : cleanedName; // Only truncate if there's an account number
  const maskedNumber = hasAccountNumber ? MaskedInstitutionAccountNumber(accountNumber) : '';
  return [name, maskedNumber].filter(Boolean).join(' ');
};

export const Capitalize: StringFormatter = (s) => {
  return s.toLowerCase().replace(/(^|\s|-)[^\s]/g, (c) => c.toUpperCase());
};

export const CardholderFirstName: CardholderFormatter = (b) => {
  return b.embossedName !== null ? b.embossedName?.split(' ')[0] : '';
};

export const PhoneNumber: StringFormatter = (v) => {
  return v
    .replace(/\D/g, '')
    .replace(/^(\d{3})(\d{1,3})/, '($1) $2')
    .replace(/^(\(\d{3}\)) (\d{3})(.+)/, '$1 $2-$3')
    .substring(0, 14);
};

const phoneNumberToString = (p: string) => {
  return p.replace(/\D/g, '').substring(0, 10);
};

export const PhoneNumberInputFormatter: TextInputFormatter<string> = {
  serialize: PhoneNumber,
  deserialize: phoneNumberToString,
};

export const SocialSecurity: StringFormatter = (v) => {
  return v
    .replace(/\D/g, '')
    .replace(/^(\d{3})(?=\d)/, '$1-')
    .replace(/^(\d{3})-(\d{2})(?=\d)/, '$1-$2-')
    .substring(0, 11);
};

const socialSecurityToString = (p: string) => {
  return p.replace(/\D/g, '').substring(0, 9);
};

export const SocialSecurityInputFormatter: TextInputFormatter<string> = {
  serialize: SocialSecurity,
  deserialize: socialSecurityToString,
};

export const DateOfBirthFormatted: StringFormatter = (v) => {
  return v
    .replace(/\D/g, '')
    .replace(/^(\d{2})(\d{1,2})/, '$1 / $2')
    .replace(/^(\d{2}) \/ (\d{2})(.+)/, '$1 / $2 / $3')
    .substring(0, 14);
};

const dateOfBirthToString = (p: string) => {
  return p.replace(/[^\d]/g, '').substring(0, 8);
};

export const DateOfBirthInputFormatter: TextInputFormatter<string> = {
  serialize: DateOfBirthFormatted,
  deserialize: dateOfBirthToString,
};

export const DateStringToInputValue = (dateStr?: string): string => {
  const yyyyMMdd = dateStr?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (yyyyMMdd) {
    const [, year, month, day] = yyyyMMdd;
    return `${month} / ${day} / ${year}`;
  }
  return '';
};

export const TrimE164CountryCode = (e164?: string): string => {
  return e164?.trim().replace('+1', '') ?? '';
};

export const E164ToPhoneInputValue = (e164?: string): string => {
  if (!e164) return '';
  const digits = e164.replace(/\D/g, '').substring(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ) ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ) ${digits.slice(3, 6)} - ${digits.slice(6)}`;
};

const StringToDollarGranularity = (p: string) => {
  // Extract digits before decimal point only
  const beforeDecimal = p.split('.')[0].replace(/\D/g, '');
  return MoneyDollarGranularity(Number(beforeDecimal));
};

const dollarToString = (p: string) => {
  return p.replace(/\D/g, '');
};

export const DollarInputFormatter: TextInputFormatter<string> = {
  serialize: StringToDollarGranularity,
  deserialize: dollarToString,
};

export const SplitCamelCase: StringFormatter = (v) => v.replace(/([a-z])([A-Z])/g, '$1 $2');

export const TitleCase: StringFormatter = (v) => v.toLowerCase().replace(/\w/, (f) => f.toUpperCase());

export const SentenceCase: StringFormatter = (v) => {
  const full = v.toLowerCase();
  return full.charAt(0).toUpperCase() + full.slice(1);
};

export const HypenCase: StringFormatter = (v) => {
  return v.replace(/([a-z])([A-Z/_])/g, '$1-$2').toLowerCase();
};

export const DecodeBase64URL = (str: string) => {
  return atob(str.replace(/_/g, '/').replace(/-/g, '+'));
};

export const FetchRedirectUrl = (path: string | string[] | undefined): string => {
  switch (typeof path) {
    case 'string':
      return path.startsWith('/') ? path : '/';
    default:
      return '/';
  }
};

export const PossessiveApostrophe: StringFormatter = (str: string) => {
  if (str.endsWith('s')) {
    return `${str}’`;
  }
  return `${str}’s`;
};

export const Pluralize = (str: string, count: number): string => {
  return count !== 1 ? `${str}s` : str;
};

export const SecondsToDuration = (seconds: number, isShort: boolean = true, pluralize: boolean = true) => {
  const minuteLabel = isShort ? 'min' : 'minute';
  const hourLabel = isShort ? 'hr' : 'hour';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const hoursStr = hours > 0 ? `${hours} ${hourLabel}${hours != 1 && pluralize ? 's' : ''} ` : '';
  const minutesStr = `${minutes} ${minuteLabel}${minutes != 1 && pluralize ? 's' : ''}`;
  return `${hoursStr}${minutesStr}`;
};

/**
 * @param d - Decimal.js Object to be formatted as a dollar amount
 * @returns Formatted dollar amount as a string, respects the precision of the Decimal.js Object with minimum 2 decimal places
 */
export const DecimalDollarFormatter: DecimalFormatter = (d: Decimal) => {
  const dp = d.decimalPlaces();
  const minDecimals = Math.max(dp, 2);

  if (d.lt(1)) return `$${d.toFixed(minDecimals)}`;

  const dollar = d.trunc().toNumber();
  const dollarFormatter = MoneyDollarGranularity(dollar);
  const cents = d.minus(d.trunc()).toFixed(minDecimals).substring(2);
  return `${dollarFormatter}.${cents}`;
};
